<template>
    <b-modal
        :visible="value"
        size="lg"
        scrollable
        hide-footer
        no-close-on-backdrop
        dialog-class="week-preview-modal"
        @hide="handleHide"
    >
        <template #modal-title>
            <div class="d-flex w-100 align-items-center">
                <span v-if="unit">
                    {{ unit.id }}: {{ unit.title || 'Untitled Week' }}
                </span>
                <span v-else>
                    Week Preview
                </span>
            </div>
        </template>

        <div v-if="loading" class="py-4 text-center">
            <b-spinner label="Loading..."></b-spinner>
        </div>

        <b-alert v-else-if="load_error" variant="danger" show>
            {{ load_error }}
        </b-alert>

        <div v-else-if="publication && unit_group && unit">
                <b-card>
                <b-card-body>
                    <div class="d-flex justify-content-end flex-wrap mb-3">
                        <router-link :to="publicationWeekRoute" class="btn btn-outline-primary btn-sm" target="_blank">
                            Open In Publication
                        </router-link>
                    </div>

                    <div class="row">
                        <div class="col-md-6 mb-3 mb-md-0">
                            <div class="mb-2">Week Info:</div>
                            <div class="alert alert-success mb-1 small">
                                SKU: {{ publication.sku || 'N/A' }}
                            </div>
                            <div class="alert alert-success mb-1 small">
                                Unit Number: {{ unit_group.order_number || 'N/A' }}
                            </div>
                            <div class="alert alert-success mb-1 small">
                                Week Number: {{ unit.order_number || 'N/A' }}
                            </div>
                            <div class="alert alert-success mb-1 small">
                                Week ID: {{ unit.id }}
                            </div>
                            <div class="alert alert-success mb-1 small">
                                Week Title: {{ unit.title || 'No Title' }}
                            </div>
                            <div class="alert alert-success mb-1 small">
                                Week Description:
                                <span v-if="unit.description" v-html="unit.description"></span>
                                <span v-else>No Description</span>
                            </div>
                        </div>

                        <div class="col-md-6">
                            <div class="mb-2">Articles:</div>
                            <template v-if="articles.length">
                                <div
                                    v-for="article in articles"
                                    :key="article.unit_page_slot_id || article.id"
                                    class="alert alert-primary mb-1 small"
                                    role="alert"
                                >
                                    {{ article.id }}:
                                    <span v-if="article.title">{{ article.title }}</span>
                                    <span v-else>No Title</span>
                                </div>
                            </template>
                            <div v-else class="alert alert-danger mb-1 small" role="alert">
                                Week contains no articles
                            </div>
                        </div>
                    </div>
                </b-card-body>
            </b-card>
        </div>
    </b-modal>
</template>

<style lang="scss">
.week-preview-modal {
    max-width: 900px;
}
</style>

<script>
export default {
    name: 'WeekPreviewModal',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        publicationId: {
            type: [Number, String],
            default: null,
        },
        unitId: {
            type: [Number, String],
            default: null,
        },
    },
    data: () => ({
        loading: false,
        load_error: null,
        publication: null,
        unit_group: null,
        unit: null,
        articles: [],
    }),
    computed: {
        publicationWeekRoute() {
            return {
                name: 'Publication',
                params: {
                    publication_id: this.publication.id,
                },
                query: {
                    unit_id: this.unit.id,
                },
            }
        },
    },
    watch: {
        value: {
            async handler(isVisible) {
                if (isVisible) {
                    await this.loadWeekContext()
                } else {
                    this.resetModalState()
                }
            },
            immediate: true,
        },
    },
    methods: {
        async loadWeekContext() {
            if (!this.publicationId || !this.unitId) {
                this.load_error = 'A publication and week are required to load this preview.'
                return
            }

            this.loading = true
            this.resetModalState()
            try {
                const response = await this.$store.dispatch('getUnitGroups', this.publicationId)
                const publications = response.data || []
                const matchedPublication = _.find(publications, publication => {
                    return Number(publication.id) === Number(this.publicationId)
                })

                if (!matchedPublication) {
                    throw new Error('Assigned publication was not found.')
                }

                const matchedUnitGroup = _.find(matchedPublication.unit_groups || [], unitGroup => {
                    return _.find(unitGroup.units || [], unit => Number(unit.id) === Number(this.unitId))
                })

                if (!matchedUnitGroup) {
                    throw new Error('Assigned unit group was not found for this week.')
                }

                const matchedUnit = _.find(matchedUnitGroup.units || [], unit => Number(unit.id) === Number(this.unitId))

                if (!matchedUnit) {
                    throw new Error('Week was not found in its assigned publication.')
                }

                this.publication = _.cloneDeep(matchedPublication)
                this.unit_group = _.cloneDeep(matchedUnitGroup)
                this.unit = _.cloneDeep(matchedUnit)
                await this.loadArticles()
            } catch (e) {
                this.load_error = e.message || 'Failed to load week preview.'
            } finally {
                this.loading = false
            }
        },
        async loadArticles() {
            const response = await this.axios.get(`/api/units/articles/${this.unitId}`)
            let articles = response.data || []
            articles = _.orderBy(articles, ['page', 'order_number'])
            this.articles = _.uniqBy(articles, 'unit_page_slot_id')
        },
        handleHide() {
            this.$emit('input', false)
        },
        resetModalState() {
            this.publication = null
            this.unit_group = null
            this.unit = null
            this.articles = []
            this.load_error = null
        },
    },
}
</script>
